import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext, useWatch } from 'react-hook-form';
import useSWR from 'swr';
import ApprovalNoticeBox from '@/components/Approval/@components/ApprovalNoticeBox';
import { SaTravelExpenseTableView } from '@/components/Approval/@components/SaTravelExpense';
import { SaTravelRequestView } from '@/components/Approval/@components/SaTravelExpense/SaTravelRequest';
import { getSaTravelExpenseDraftSourceUrl } from '@/constant/approval';
import { getNestedDataFetcher } from '@/utils/apis';
import {
  buildSaTravelSubject,
  isSaTravelRequestType,
  pickSaTravelHeader,
  SA_TRAVEL_MISSING_ENTRY_MESSAGE,
  type SaTravelDraftSource,
  type SaTravelEntry,
} from '@/utils/approval/saTravelExpense';
import { FieldBlock } from './styles';
import type { ApprovalFormValues } from './types';

type SaTravelExpenseFieldProps = {
  apvType: number;
  /** 진입 쿼리 sa_docno·sa_ztype — 신규 작성 전용 */
  entry?: SaTravelEntry;
};

/**
 * SA 신규 출장비 정산(177)·사전 신청(179) — 레거시 appr_edit_forms_eas_new_travel·eas_confirm_travel.
 * 사용자 입력 없이 진입 쿼리로 SAP 출장 데이터를 받아 표·제목을 채움
 */
const SaTravelExpenseField = ({
  apvType,
  entry,
}: SaTravelExpenseFieldProps) => {
  const { t } = useTranslation();
  const { control, getValues, setValue } = useFormContext<ApprovalFormValues>();
  const state = useWatch({ control, name: 'saTravelExpense' });

  const { data, isLoading } = useSWR<SaTravelDraftSource>(
    entry
      ? getSaTravelExpenseDraftSourceUrl(
          apvType,
          entry.documentNumber,
          entry.zType,
        )
      : null,
    getNestedDataFetcher,
    { revalidateOnFocus: false, shouldRetryOnError: false },
  );

  // 제목은 SAP 응답을 받을 때 한 번만(레거시 동일) — 언어 변경 등으로 다시 돌 때 사용자가 고친 제목을 덮지 않게
  const subjectSourceRef = useRef<SaTravelDraftSource | null>(null);

  useEffect(() => {
    const header = pickSaTravelHeader(data);
    if (!entry || !header) {
      setValue('saTravelExpense', null, { shouldDirty: false });
      return;
    }
    const isNewSource = subjectSourceRef.current !== data;
    const autoSubject =
      (!isNewSource && getValues('saTravelExpense')?.autoSubject) ||
      buildSaTravelSubject(apvType, header, t);
    setValue(
      'saTravelExpense',
      {
        sapDocumentNo: entry.documentNumber,
        zType: entry.zType,
        header,
        autoSubject,
      },
      { shouldDirty: false },
    );
    if (!isNewSource) return;
    subjectSourceRef.current = data ?? null;
    setValue('subject', autoSubject, { shouldDirty: false });
  }, [apvType, data, entry, getValues, setValue, t]);

  const renderBody = () => {
    if (state)
      return isSaTravelRequestType(apvType) ? (
        <SaTravelRequestView header={state.header} t={t} />
      ) : (
        <SaTravelExpenseTableView header={state.header} t={t} />
      );
    // 주소에 sa_docno·sa_ztype 가 없으면 SAP 조회 자체를 안 함 — 조회 실패와 구분해 진입 경로를 안내
    if (!entry)
      return (
        <ApprovalNoticeBox role="alert">
          {t(SA_TRAVEL_MISSING_ENTRY_MESSAGE)}
        </ApprovalNoticeBox>
      );
    if (isLoading)
      return (
        <ApprovalNoticeBox role="status" aria-busy>
          {t('데이터를 불러오는 중입니다...')}
        </ApprovalNoticeBox>
      );
    return (
      <ApprovalNoticeBox role="alert">
        {t('데이터를 불러오지 못했습니다.')}
      </ApprovalNoticeBox>
    );
  };

  return <FieldBlock className="sa-travel-expense">{renderBody()}</FieldBlock>;
};

export default SaTravelExpenseField;
